import { z } from 'zod';

// Each value is an order source: the provider, or the brand for POS orders.
export const REPORT_PROVIDERS = ['pos', 'kiosk', 'food2'];

// Payment tender categories the POS performance report can be narrowed to.
// None selected means all of them.
export const TENDER_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'reward', label: 'Reward' },
  { value: 'other', label: 'Other' },
];

export const TENDER_TYPE_VALUES = TENDER_TYPES.map(({ value }) => value);

// Whether a payment method row belongs in a report requested with
// `tenderTypes`. No tender filter on the request shows every method.
export const isTenderTypeShown = (tenderTypes, tenderType) =>
  !Array.isArray(tenderTypes) ||
  tenderTypes.length === 0 ||
  tenderTypes.includes(tenderType);

export const INCLUDE_SECTION_VALUES = [
  'deliveryFeeBreakdown',
  'tipBreakdown',
  'kitchenTipBreakdown',
  'driverTipBreakdown',
  'driverBreakdown',
  'taxBreakdown',
  'refundsBreakdown',
  'ordersCoDispatchBreakdown',
  'paymentMethods',
  'deliveryPaymentMethods',
  'dailyBreakdown',
  'detailedReport',
  'employeeDetailedBreakdown',
  'customerReport',
  'employeeTimesheetReport',
  'totalProfitReport',
];

export const INCLUDE_SECTIONS = [
  { value: 'deliveryFeeBreakdown', label: 'Delivery Fee Breakdown' },
  { value: 'tipBreakdown', label: 'Tip Breakdown' },
  { value: 'kitchenTipBreakdown', label: 'Kitchen Tip Breakdown' },
  { value: 'driverTipBreakdown', label: 'Driver Tip Breakdown' },
  { value: 'driverBreakdown', label: 'Driver Performance' },
  { value: 'taxBreakdown', label: 'Tax Breakdown' },
  { value: 'refundsBreakdown', label: 'Refunds Breakdown' },
  {
    value: 'ordersCoDispatchBreakdown',
    label: 'Orders.co Dispatch Delivery Breakdown',
  },
  { value: 'paymentMethods', label: 'Payment Methods' },
  { value: 'deliveryPaymentMethods', label: 'Delivery Payment Methods' },
  { value: 'dailyBreakdown', label: 'Daily Performance Breakdown' },
  { value: 'detailedReport', label: 'Detailed Orders' },
  { value: 'employeeDetailedBreakdown', label: 'Employee Sales Performance' },
  { value: 'customerReport', label: 'Customer Analytics' },
  { value: 'employeeTimesheetReport', label: 'Employee/Timesheet Report' },
  { value: 'totalProfitReport', label: 'Total Profit Report' },
];

export const DEFAULT_INCLUDE_SECTIONS = INCLUDE_SECTION_VALUES.filter(
  (v) => v !== 'totalProfitReport'
);

export const EXCLUDED_SUMMARY_REPORT_SECTIONS = [''];

export const EXCLUDED_DELIVERY_REPORT_SECTIONS = [
  'tipBreakdown',
  'kitchenTipBreakdown',
  'employeeTimesheetReport',
  'totalProfitReport',
  'employeeDetailedBreakdown',
];

export const getTabIncludeSections = (excluded = []) =>
  INCLUDE_SECTIONS.filter(({ value }) => !excluded.includes(value));

export const getTabIncludeValues = (excluded = []) =>
  INCLUDE_SECTION_VALUES.filter((value) => !excluded.includes(value));

export const mergeIncludeGroupSelection = (
  current = [],
  groupValues = [],
  nextGroupSelection = []
) => {
  const groupSet = new Set(groupValues);
  const kept = current.filter((value) => !groupSet.has(value));
  const next = nextGroupSelection.filter((value) => groupSet.has(value));
  return [...kept, ...next];
};

export const isReportSectionEnabled = (include, section, hasData = false) => {
  if (Array.isArray(include) && include.length > 0) {
    return include.includes(section);
  }
  return Boolean(hasData);
};

// Report request schema
export const reportRequestSchema = z.object({
  businessUuid: z.string().uuid('Invalid business UUID format'),
  businessId: z.string().optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  startTime: z
    .string()
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format')
    .default('00:00'),
  endTime: z
    .string()
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format')
    .default('23:59'),
  filter: z.enum(['paid', 'all']).default('paid'),
  useLocalTime: z.boolean().default(false), // true for local time, false for UTC
  includeModifierOption: z.boolean().default(false), // Include modifier options in report
  // Optional provider filtering: list of provider ids or preset tags
  providers: z
    .array(
      z.enum([
        ...REPORT_PROVIDERS,
        // presets
        'all',
        'all_third_party',
        'all_first_party',
      ])
    )
    .optional()
    .default([]),
  tenderTypes: z.array(z.enum(TENDER_TYPE_VALUES)).optional().default([]),
  include: z.array(z.enum(INCLUDE_SECTION_VALUES)).optional(),
});

// API response schemas matching the real POS API structure
export const dateRangeSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
});

export const tenderTypeSchema = z.object({
  _id: z.string(),
  count: z.number(),
  countWithTip: z.number(),
  totalAmount: z.number(),
  totalTip: z.number(),
});

export const cardTypeBreakdownSchema = z.object({
  cardType: z.string(),
  count: z.number(),
  totalAmount: z.number(),
  totalTip: z.number(),
  averageAmount: z.number(),
  percentageOfCardTransactions: z.number(),
});

export const salesBySourceSchema = z.object({
  _id: z.string(),
  count: z.number(),
  countWithTip: z.number(),
  subTotal: z.number(),
  tax: z.number(),
  total: z.number(),
  deliveryFee: z.number(),
  totalTip: z.number(),
});

export const serviceFeesSchema = z.object({
  _id: z.null(),
  totalOrdersCoFee: z.number(),
  totalMarketplaceFee: z.number(),
  totalProcessingFee: z.number(),
});

export const moneyReportSummarySchema = z.object({
  _id: z.null(),
  totalOrders: z.number(),
  totalOrdersWithTip: z.number(),
  totalSubTotal: z.number(),
  totalTax: z.number(),
  totalDeliveryFee: z.number(),
  totalTip: z.number(),
  totalAmount: z.number(),
});

// New breakdown schemas
export const orderBreakdownSchema = z.object({
  _id: z.string().nullable(),
  count: z.number(),
  countWithTip: z.number(),
  subTotal: z.number(),
  tax: z.number(),
  total: z.number(),
  deliveryFee: z.number(),
  totalTip: z.number(),
});

export const moneyReportSchema = z.object({
  tenderTypes: z.array(tenderTypeSchema),
  cardTypeBreakdown: z.array(cardTypeBreakdownSchema).optional(),
  salesBySource: z.array(salesBySourceSchema),
  byOrderType: z.array(orderBreakdownSchema),
  byPaymentMethod: z.array(orderBreakdownSchema),
  byProvider: z.array(orderBreakdownSchema),
  byState: z.array(orderBreakdownSchema),
  byStatus: z.array(orderBreakdownSchema),
  serviceFees: serviceFeesSchema,
  summary: moneyReportSummarySchema,
});

export const driverTipsSchema = z.object({
  orderCount: z.number(),
  deliveryProvider: z.string(),
  totalDriverTips: z.number(),
  totalTips: z.number().nullable(),
  averageTipPerOrder: z.number(),
});

export const deliveryCostsSchema = z.object({
  orderCount: z.number(),
  deliveryProvider: z.string(),
  totalDeliveryFees: z.number(),
  totalDeliveryQuotes: z.number(),
  averageDeliveryFee: z.number(),
});

export const deliveryPricesSchema = z.object({
  orderCount: z.number(),
  deliveryProvider: z.string(),
  totalCustomerDeliveryFees: z.number(),
  averageCustomerDeliveryFee: z.number(),
});

export const tipBreakdownItemSchema = z.object({
  tenderType: z.string(),
  tipCount: z.number(),
  totalTips: z.number(),
  averageTip: z.number(),
});

export const tipBreakdownByTenderSchema = z.object({
  tipBreakdown: z.array(tipBreakdownItemSchema),
  deliveryProvider: z.string(),
});

export const thirdPartyProviderBreakdownSchema = z.object({
  provider: z.string(),
  orderCount: z.number(),
  totalDeliveryFees: z.number(),
  totalTips: z.number(),
  averageDeliveryFee: z.number(),
  averageTip: z.number(),
});

export const thirdPartyBreakdownSchema = z.object({
  providerBreakdown: z.array(thirdPartyProviderBreakdownSchema),
  deliveryProvider: z.string(),
});

export const driverBreakdownItemSchema = z.object({
  orderCount: z.number(),
  firstDelivery: z.string(),
  lastDelivery: z.string(),
  assignmentTimes: z.array(z.string()),
  driverUuid: z.string(),
  driverName: z.string(),
  driverPhone: z.string(),
  driverEmail: z.string(),
  totalDriverTips: z.number(),
  totalAllTips: z.number(),
  totalDeliveryFees: z.number(),
  totalOrderValue: z.number(),
  averageOrderValue: z.number(),
  averageDriverTip: z.number(),
  averageAllTips: z.number(),
  averageDeliveryFee: z.number(),
  totalDistance: z.number(),
  averageDistance: z.number(),
  totalDuration: z.number(),
  averageDuration: z.number(),
  tipPercentage: z.number(),
  driverTipPercentage: z.number(),
  deliveryEfficiency: z.number(),
});

export const deliveryReportSchema = z.object({
  driverTips: z.array(driverTipsSchema),
  deliveryCosts: z.array(deliveryCostsSchema),
  deliveryPrices: z.array(deliveryPricesSchema),
  tipBreakdownByTender: z.array(tipBreakdownByTenderSchema),
  thirdPartyBreakdown: z.array(thirdPartyBreakdownSchema),
  driverBreakdown: z.array(driverBreakdownItemSchema),
});

export const couponSchema = z.object({
  count: z.number(),
  _id: z.string(),
  couponId: z.string(),
  couponCode: z.string(),
  couponName: z.string(),
  couponTitle: z.string(),
  totalCouponAmount: z.number(),
});

export const discountSchema = z.object({
  _id: z.string(),
  count: z.number(),
  totalDiscountAmount: z.number(),
});

export const refundSchema = z.object({
  _id: z.string(),
  count: z.number(),
  totalRefundAmount: z.number(),
});

export const rewardsSchema = z.object({
  totalUsedRewardPoints: z.number(),
  totalEarnedRewardPoints: z.number(),
  totalUsedRewards: z.number(),
  totalEarnedRewards: z.number(),
});

export const couponsDiscountsSummarySchema = z.object({
  coupons: z.object({
    totalCount: z.number(),
    totalAmount: z.number(),
  }),
  discounts: z.object({
    totalCount: z.number(),
    totalAmount: z.number(),
  }),
  refunds: z.object({
    totalCount: z.number(),
    totalAmount: z.number(),
  }),
  rewards: z.object({
    totalUsedRewards: z.number(),
    totalEarnedRewards: z.number(),
    totalUsedRewardPoints: z.number(),
    totalEarnedRewardPoints: z.number(),
  }),
});

export const couponsDiscountsRewardsSchema = z.object({
  coupons: z.array(couponSchema),
  discounts: z.array(discountSchema),
  refunds: z.array(refundSchema),
  rewards: rewardsSchema,
  summary: couponsDiscountsSummarySchema,
});

export const customerTypeSchema = z.object({
  _id: z.string(),
  count: z.number(),
  orderCount: z.number(),
  totalSpent: z.number(),
  averageSpent: z.number(),
  countWithTip: z.number(),
  totalSpentWithTip: z.number(),
  averageSpentWithTip: z.number().nullable(),
});

export const customerSummarySchema = z.object({
  totalCustomers: z.number(),
  totalRegisteredCustomers: z.number(),
  totalNewCustomers: z.number(),
  totalReturningCustomers: z.number(),
  totalGuestCustomers: z.number(),
  totalOrders: z.number().nullable(),
  totalSpent: z.number(),
  totalRevenue: z.number(),
  averageOrderValue: z.number().nullable(),
  totalSpentWithTip: z.number(),
  averageSpentWithTip: z.number().nullable(),
});

export const customersReportSchema = z.object({
  guestCustomers: customerTypeSchema,
  newCustomers: customerTypeSchema,
  returningCustomers: customerTypeSchema,
  customerSummary: customerSummarySchema,
  conversionRate: z.number(),
  retentionRate: z.number(),
});

// New standardized customer report schema
export const customerReportSchema = z.object({
  summary: z.object({
    totalCustomers: z.number(),
    totalRegisteredCustomers: z.number(),
    totalNewCustomers: z.number(),
    totalReturningCustomers: z.number(),
    totalGuestCustomers: z.number(),
    totalOrders: z.number().nullable(),
    totalSpent: z.number(),
    totalRevenue: z.number(),
    averageOrderValue: z.number().nullable(),
    totalSpentWithTip: z.number(),
    averageSpentWithTip: z.number().nullable(),
  }),
  conversionRate: z.number().nullable(),
  retentionRate: z.number(),
  breakdownBySegment: z.array(
    z.object({
      segment: z.string(),
      customerCount: z.number(),
      orderCount: z.number(),
      totalSpent: z.number(),
      averageSpent: z.number(),
      ordersWithTip: z.number(),
      totalSpentWithTip: z.number(),
      averageSpentWithTip: z.number(),
      percentageOfTotalCustomers: z.number(),
      percentageOfTotalRevenue: z.number(),
    })
  ),
});

export const sourceBreakdownSchema = z.object({
  _id: z.string(),
  orderCount: z.number(),
  deliveryOrders: z.number(),
  deliveryOrdersWithTip: z.number(),
  pickupOrders: z.number(),
  pickupOrdersWithTip: z.number(),
  dineInOrders: z.number(),
  dineInOrdersWithTip: z.number(),
  subTotal: z.number(),
  tax: z.number(),
  deliveryFee: z.number(),
  tip: z.number(),
  kitchenTip: z.number(),
  driverTip: z.number(),
  total: z.number(),
  discount: z.number(),
  rewardAmount: z.number(),
  refundAmount: z.number(),
  averageOrderValue: z.number(),
  percentageOfTotal: z.number(),
});

// Tip Summary schemas
export const tipOverviewSchema = z.object({
  totalTip: z.number(),
  totalKitchenTip: z.number(),
  totalDriverTip: z.number(),
  totalOrders: z.number(),
  totalOrdersWithTip: z.number(),
  averageTipPerOrder: z.number(),
  averageTipPerOrderWithTips: z.number(),
  tipParticipationRate: z.number(),
  tipPercentageOfSales: z.number(),
  tipDistribution: z.object({
    kitchenTipPercentage: z.number(),
    driverTipPercentage: z.number(),
  }),
});

export const tipByPaymentTypeSchema = z.object({
  paymentType: z.string(),
  tipAmount: z.number(),
  orderCount: z.number(),
  averageTipPerOrder: z.number(),
  tipPercentageOfSales: z.number(),
  tipPercentageOfTotal: z.number(),
});

export const tipBySourceSchema = z.object({
  source: z.string(),
  tipAmount: z.number(),
  orderCount: z.number(),
  averageTipPerOrder: z.number(),
  kitchenTipAmount: z.number(),
  driverTipAmount: z.number(),
  averageKitchenTip: z.number(),
  averageDriverTip: z.number(),
  tipPercentageOfSales: z.number(),
  tipPercentageOfTotal: z.number(),
});

export const tipReportSchema = z.object({
  summary: tipOverviewSchema,
  // Support both old and new field names
  byPaymentType: z.array(tipByPaymentTypeSchema).optional(),
  bySource: z.array(tipBySourceSchema).optional(),
  breakdownByPaymentType: z.array(tipByPaymentTypeSchema).optional(),
  breakdownBySource: z.array(tipBySourceSchema).optional(),
});

// Legacy support - will be deprecated
export const tipSummarySchema = tipReportSchema;

// New standardized report schemas
export const orderBreakdownBySourceSchema = z.object({
  summary: z.object({
    totalProviders: z.number(),
    totalOrders: z.number(),
    totalOrdersWithTip: z.number(),
    totalRevenue: z.number(),
    totalTips: z.number(),
    totalDeliveryFees: z.number(),
    totalDiscounts: z.number(),
    averageOrderValue: z.number(),
  }),
  breakdown: z.array(
    z.object({
      provider: z.string(),
      orderCount: z.number(),
      orderCountWithTip: z.number(),
      subTotal: z.number(),
      tax: z.number(),
      deliveryFee: z.number(),
      tip: z.number(),
      kitchenTip: z.number(),
      driverTip: z.number(),
      total: z.number(),
      discount: z.number(),
      rewardAmount: z.number(),
      refundAmount: z.number(),
      averageOrderValue: z.number(),
      averageOrderValueWithTip: z.number(),
      deliveryOrders: z.number(),
      deliveryOrdersWithTip: z.number(),
      pickupOrders: z.number(),
      pickupOrdersWithTip: z.number(),
      dineInOrders: z.number(),
      dineInOrdersWithTip: z.number(),
      inStoreOrders: z.number(),
      inStoreOrdersWithTip: z.number(),
    })
  ),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    timezone: z.string(),
  }),
});

export const orderBreakdownByDateSchema = z.object({
  summary: z.object({
    grossSales: z.number(),
    discounts: z.number(),
    refunds: z.number(),
    netSales: z.number(),
    nonRevenueItems: z.number(),
    taxesExpected: z.number(),
    taxesCollected: z.number(),
    tips: z.number(),
    amountCollected: z.number(),
    unpaidBalance: z.number(),
    orderCount: z.number(),
    ordersWithTip: z.number(),
  }),
  breakdown: z.array(
    z.object({
      _id: z.string(), // date in YYYY-MM-DD format
      orderCount: z.number(),
      ordersWithTip: z.number(),
      grossSales: z.number(),
      discounts: z.number(),
      refunds: z.number(),
      netSales: z.number(),
      nonRevenueItems: z.number(),
      taxesExpected: z.number(),
      taxesCollected: z.number(),
      tips: z.number(),
      amountCollected: z.number(),
      unpaidBalance: z.number(),
    })
  ),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    timezone: z.string(),
  }),
});

export const customerBreakdownSchema = z.object({
  summary: z.object({
    totalCustomers: z.number(),
    totalSales: z.number(),
    totalOrders: z.number(),
    averageOrderValue: z.number(),
    averageSalesPerCustomer: z.number(),
  }),
  breakdown: z.array(
    z.object({
      uuid: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      phoneNumber: z.string(),
      email: z.string(),
      registrationDate: z.string(),
      totalSales: z.number(),
      orderCount: z.number(),
      averageOrderValue: z.number(),
      firstOrderDate: z.string(),
      lastOrderDate: z.string(),
    })
  ),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    timezone: z.string(),
  }),
});

export const rewardReportSchema = z.object({
  summary: z.object({
    totalUsedRewards: z.number(),
    totalEarnedRewards: z.number(),
    totalUsedRewardPoints: z.number(),
    totalEarnedRewardPoints: z.number(),
    orderCount: z.number(),
  }),
  rewards: z.object({
    totalUsedRewards: z.number(),
    totalEarnedRewards: z.number(),
    totalUsedRewardPoints: z.number(),
    totalEarnedRewardPoints: z.number(),
    orderCount: z.number(),
  }),
});

export const discountReportSchema = z.object({
  summary: z.object({
    discounts: z.object({
      totalCount: z.number(),
      totalAmount: z.number(),
    }),
    refunds: z.object({
      totalCount: z.number(),
      totalAmount: z.number(),
    }),
  }),
  discounts: z.array(z.unknown()),
  refunds: z.array(
    z.object({
      _id: z.string(),
      count: z.number(),
      totalRefundAmount: z.number(),
    })
  ),
});

export const couponsReportSchema = z.object({
  summary: z.object({
    totalCount: z.number(),
    totalAmount: z.number(),
  }),
  coupons: z.array(
    z.object({
      count: z.number(),
      _id: z.string(),
      couponId: z.string(),
      couponCode: z.string(),
      couponName: z.string(),
      couponTitle: z.string(),
      totalCouponAmount: z.number(),
    })
  ),
});

export const orderReportSchema = z.object({
  summary: z.object({
    totalOrders: z.number(),
    totalOrdersWithTip: z.number(),
    totalRevenue: z.number(),
    totalSubTotal: z.number(),
    totalTax: z.number(),
    totalTip: z.number(),
    totalDeliveryFee: z.number(),
    totalDiscount: z.number(),
    totalRefund: z.number(),
    averageOrderValue: z.number(),
    averageSubTotal: z.number(),
    averageTip: z.number(),
    averageTax: z.number(),
    tipPercentage: z.number(),
  }),
  byOrderType: z.array(
    z.object({
      count: z.number(),
      countWithTip: z.number(),
      orderType: z.string(),
      totalRevenue: z.number(),
      totalTip: z.number(),
      averageOrderValue: z.number(),
    })
  ),
  byProvider: z.array(
    z.object({
      count: z.number(),
      countWithTip: z.number(),
      provider: z.string(),
      totalRevenue: z.number(),
      totalTip: z.number(),
      averageOrderValue: z.number(),
    })
  ),
  byStatus: z.array(
    z.object({
      count: z.number(),
      status: z.string(),
      totalRevenue: z.number(),
      averageOrderValue: z.number(),
    })
  ),
});

// Daily breakdown schema
export const dailyBreakdownItemSchema = z.object({
  _id: z.string().optional(), // date in YYYY-MM-DD format
  date: z.string().optional(), // Alternative field name
  todaysTotalOrders: z.number().optional(),
  orderCount: z.number().optional(),
  ordersWithTip: z.number().optional(),
  grossSales: z.number().optional(),
  subtotal: z.number().optional(),
  discounts: z.number().optional(),
  refunds: z.number().optional(),
  totalRefund: z.number().optional(),
  netSales: z.number().optional(),
  nonRevenueItems: z.number().optional(),
  tax: z.number().optional(),
  taxesExpected: z.number().optional(),
  taxesCollected: z.number().optional(),
  adjustmentsAndFees: z.number().optional(),
  totalDeliveryFeeProcessed: z.number().optional(),
  kitchenTip: z.number().optional(),
  driverTip: z.number().optional(),
  totalTipProcessed: z.number().optional(),
  tips: z.number().optional(),
  totalDiscounts: z.number().optional(),
  total: z.number().optional(),
  unpaidAmount: z.number().optional(),
  unpaidBalance: z.number().optional(),
  amountCollected: z.number().optional(),
});

export const dailyBreakdownTotalsSchema = z.object({
  grossSales: z.number(),
  discounts: z.number(),
  refunds: z.number(),
  netSales: z.number(),
  nonRevenueItems: z.number(),
  taxesExpected: z.number(),
  taxesCollected: z.number(),
  tips: z.number(),
  amountCollected: z.number(),
  unpaidBalance: z.number(),
  orderCount: z.number(),
  ordersWithTip: z.number(),
});

export const dailyBreakdownDateRangeSchema = z.object({
  start: z.string(),
  end: z.string(),
});

export const dailyBreakdownSchema = z.object({
  dailyBreakdown: z.array(dailyBreakdownItemSchema),
  totals: dailyBreakdownTotalsSchema,
  dateRange: dailyBreakdownDateRangeSchema,
});

// Business information schema
export const businessInfoSchema = z.object({
  businessTitle: z.string(),
  businessDescription: z.string().optional(),
  businessAddressInfo: z.object({
    fullAddress: z.string().optional(),
    address: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().optional(),
    geoLocation: z.unknown().optional(),
  }),
  businessAdminFirstName: z.string(),
  businessAdminLastName: z.string(),
  businessAdminPhone: z.string(),
});

// Detailed Order Report schemas
export const detailedOrderItemSchema = z.object({
  _id: z.string().optional(),
  actualPlacedDate: z.string().optional(),
  orderUuid: z.string().optional(),
  orderNumber: z.string().optional(),
  orderType: z.string().optional(),
  total: z.number().optional(),
  subtotal: z.number().optional(),
  tax: z.number().optional(),
  deliveryFee: z.number().optional(),
  kitchenTip: z.number().optional(),
  driverTip: z.number().optional(),
  discount: z.number().optional(),
  balanceDue: z.number().optional(),
  deliveryProvider: z.string().nullable().optional(),
  order: z
    .object({
      _id: z.string(),
      orderType: z.string(),
      displayNumber: z.string(),
      provider: z.string(),
      providerBrand: z.string().nullable().optional(),
      placedAt: z.string(),
      charges: z.object({
        taxAmount: z.number(),
        tip: z.number(),
        subTotal: z.number(),
        deliveryFee: z.number(),
        total: z.number(),
        discount: z.number(),
        rewardAmount: z.number(),
        rewardPoints: z.number(),
        earnedRewardAmount: z.number(),
        earnedRewardPoints: z.number(),
        discountDetails: z.array(z.unknown()),
        tipInfo: z.object({
          driver: z.number(),
          kitchen: z.number(),
        }),
        couponAmount: z.number(),
        balanceDue: z.number(),
        servicingFee: z.object({
          ordersCoFee: z.number(),
          marketplaceFee: z.number(),
          processingFee: z.number(),
        }),
        refundAmount: z.number(),
        compAmount: z.number(),
      }),
    })
    .optional(),
});

export const transactionItemSchema = z.object({
  transactionUuid: z.string(),
  orderUuid: z.string(),
  transactionType: z.string(),
  status: z.string(),
  createdAt: z.string(),
  totalAmount: z.number(),
  tenderType: z.string(),
  kitchenTipAmount: z.number(),
  driverTipAmount: z.number(),
  taxAmount: z.number(),
});

export const detailedOrderSummarySchema = z.object({
  totalOrders: z.number(),
  totalTransactions: z.number().optional(),
  totalRevenue: z.number().optional(),
  totalTips: z.number().optional(),
  totalItems: z.number().optional(),
  paymentMethodBreakdown: z.record(z.number()).optional(),
  orderTypeBreakdown: z.record(z.number()).optional(),
});

export const detailedOrderReportSchema = z.object({
  summary: detailedOrderSummarySchema.optional(),
  totalOrders: z.number().optional(),
  totalTransactions: z.number().optional(),
  orders: z.array(detailedOrderItemSchema),
  transactions: z.array(transactionItemSchema).optional(),
});

// NEW POS Performance Response Schema
export const deliveryFeeBreakdownSchema = z.object({
  selfDelivery: z.number(),
  ordersCoDispatch: z.number(),
  daily: z.array(z.unknown()),
});

export const tipBreakdownDailyItemSchema = z.object({
  date: z.string(),
  kitchenTip: z.number(),
  driverTip: z.number(),
  selfDelivery: z.number(),
  ordersCoDispatch: z.number(),
});

export const posPerformanceTipBreakdownSchema = z.object({
  kitchenTip: z.number(),
  driverTip: z.number(),
  selfDelivery: z.number(),
  ordersCoDispatch: z.number(),
  daily: z.array(tipBreakdownDailyItemSchema),
});

export const paymentMethodsDailyItemSchema = z.object({
  date: z.string(),
  card: z.number(),
  cash: z.number(),
  other: z.number(),
  rewardsUsed: z.number(),
});

export const paymentMethodsBreakdownSchema = z.object({
  card: z.number(),
  cash: z.number(),
  other: z.number(),
  rewardsUsed: z.number(),
  daily: z.array(paymentMethodsDailyItemSchema),
});

// Order source: the provider, or the provider brand for POS orders ('pos' when
// the brand is empty), lowercased.
export const sourceValueSchema = z.object({
  source: z.string(),
  value: z.number(),
});

export const bySourceSchema = z.object({
  sources: z.array(sourceValueSchema),
  daily: z.array(
    z.object({
      date: z.string(),
      sources: z.array(sourceValueSchema),
    })
  ),
});

const paymentMethodsBySourceFields = {
  cardBySource: z.array(sourceValueSchema),
  cashBySource: z.array(sourceValueSchema),
  otherBySource: z.array(sourceValueSchema),
  rewardsUsedBySource: z.array(sourceValueSchema),
};

export const paymentMethodsWithSourceSchema =
  paymentMethodsBreakdownSchema.extend({
    ...paymentMethodsBySourceFields,
    daily: z.array(
      paymentMethodsDailyItemSchema.extend(paymentMethodsBySourceFields)
    ),
  });

export const kitchenTipBreakdownDailyItemSchema = z.object({
  date: z.string(),
  purchase: z.number(),
  refund: z.number(),
});

export const kitchenTipBreakdownSchema = z.object({
  kitchenTipPaid: z.number(),
  kitchenTipRefunded: z.number(),
  kitchenTipCollected: z.number(),
  daily: z.array(kitchenTipBreakdownDailyItemSchema),
});

export const driverTipBreakdownSchema = z.object({
  driverTipPaid: z.number(),
  driverTipRefunded: z.number(),
  driverTipCollected: z.number(),
  daily: z.array(kitchenTipBreakdownDailyItemSchema),
});

export const taxBreakdownSchema = z.object({
  taxProcessed: z.number(),
  priorTaxRefunded: z.number(),
  taxCollected: z.number(),
  daily: z.array(kitchenTipBreakdownDailyItemSchema),
});

export const refundsBreakdownSchema = z.object({
  todaysOrderRefunds: z.number(),
  priorOrderRefunds: z.number(),
  totalRefundProcessed: z.number(),
  daily: z.array(z.unknown()),
});

export const ordersCoDispatchBreakdownSchema = z.object({
  deliveryCost: z.number(),
  deliveryTip: z.number(),
  amountDueToOrdersCo: z.number(),
  daily: z.array(z.unknown()),
});

// Total Profit Report (POS Performance)
export const totalProfitReportItemSchema = z.object({
  externalId: z.string(),
  itemName: z.string(),
  quantity: z.number(),
  salePrice: z.number(),
  unitCost: z.number(),
  totalSaleValue: z.number(),
  totalCost: z.number(),
});

export const totalProfitReportOrderSchema = z.object({
  orderUuid: z.string(),
  orderNumber: z.string(),
  displayNumber: z.string(),
  date: z.string(),
  totalSalesValue: z.number(),
  costOfGoodsSold: z.number(),
  grossProfit: z.number(),
  tax: z.number(),
  items: z.array(totalProfitReportItemSchema),
});

export const totalProfitReportDailyItemSchema = z.object({
  date: z.string(),
  totalSalesValue: z.number(),
  subtotal: z.number().optional(),
  costOfGoodsSold: z.number(),
  grossProfit: z.number(),
  grossProfitPercent: z.number().optional(),
  totalTax: z.number(),
});

export const totalProfitReportSchema = z.object({
  totalSalesValue: z.number(),
  totalSubtotal: z.number().optional(),
  costOfGoodsSold: z.number(),
  grossProfit: z.number(),
  grossProfitPercent: z.number().optional(),
  totalTax: z.number(),
  daily: z.array(totalProfitReportDailyItemSchema),
  orders: z.array(totalProfitReportOrderSchema),
});

export const posPerformanceDailyBreakdownItemSchema = z.object({
  todaysTotalOrders: z.number(),
  date: z.string(),
  subtotal: z.number(),
  tax: z.number(),
  adjustmentsAndFees: z.number(),
  totalDeliveryFeeProcessed: z.number(),
  kitchenTip: z.number(),
  driverTip: z.number(),
  totalTipProcessed: z.number(),
  totalDiscounts: z.number(),
  totalRefund: z.number(),
  total: z.number(),
  unpaidAmount: z.number(),
  amountCollected: z.number(),
});

// Main POS Performance Response Schema
export const posPerformanceResponseSchema = z.object({
  dateRange: dateRangeSchema,
  filters: reportRequestSchema.optional(),
  // Always returned regardless of include
  todaysTotalOrders: z.number(),
  totalOrdersBySource: bySourceSchema,
  subtotal: z.number(),
  subtotalBySource: bySourceSchema,
  amountCollectedBySource: bySourceSchema,
  tax: z.number(),
  adjustmentsAndFees: z.number(),
  totalDiscounts: z.number(),
  totalCompAmount: z.number().optional(),
  unpaidAmount: z.number(),
  businessInfo: businessInfoSchema,
  // Returned only when parent breakdown is included
  totalDeliveryFeeProcessed: z.number().optional(),
  deliveryFeeBreakdown: deliveryFeeBreakdownSchema.optional(),
  totalTipProcessed: z.number().optional(),
  tipBreakdown: posPerformanceTipBreakdownSchema.optional(),
  totalRefund: z.number().optional(),
  total: z.number().optional(),
  amountCollected: z.number().optional(),
  perOrderSalesCost: z.number().optional(),
  paymentMethods: paymentMethodsWithSourceSchema.optional(),
  deliveryPaymentMethods: paymentMethodsBreakdownSchema.optional(),
  kitchenTipBreakdown: kitchenTipBreakdownSchema.optional(),
  driverTipBreakdown: driverTipBreakdownSchema.optional(),
  taxBreakdown: taxBreakdownSchema.optional(),
  refundsBreakdown: refundsBreakdownSchema.optional(),
  ordersCoDispatchBreakdown: ordersCoDispatchBreakdownSchema.optional(),
  dailyBreakdown: z.array(posPerformanceDailyBreakdownItemSchema).optional(),
  detailedReport: detailedOrderReportSchema.optional(),
  customerReport: customerReportSchema.optional(),
  employeeDetailedBreakdown: z.unknown().optional(),
  employeeTimesheetReport: z.unknown().optional(),
  totalProfitReport: totalProfitReportSchema.optional(),
});

// Sales By Category Response Schema
export const salesByCategoryItemSchema = z.object({
  itemId: z.string().nullable(),
  itemName: z.string(),
  totalQuantity: z.number(),
  avgPrice: z.number(),
  totalRevenue: z.number(),
  percentOfCategoryRevenue: z.number(),
});

export const salesByCategorySchema = z.object({
  // Items with a missing or blank menu category are grouped under "Uncategorized"
  category: z.string(),
  // Distinct products; sizes are modifier options so they are already collapsed
  variantCount: z.number(),
  totalQuantity: z.number(),
  totalRevenue: z.number(),
  percentOfTotalRevenue: z.number(),
  items: z.array(salesByCategoryItemSchema),
});

export const salesByCategorySummarySchema = z.object({
  totalCategories: z.number(),
  totalQuantitySold: z.number(),
  totalRevenue: z.number(),
});

export const salesByCategoryResponseSchema = z.object({
  summary: salesByCategorySummarySchema,
  categories: z.array(salesByCategorySchema),
  businessInfo: businessInfoSchema.nullable().optional(),
  filters: reportRequestSchema.optional(),
});

export const reportDataSchema = z.object({
  dateRange: dateRangeSchema,
  moneyReport: moneyReportSchema.optional(),
  deliveryReport: deliveryReportSchema.optional(),
  couponsDiscountsRewards: couponsDiscountsRewardsSchema.optional(),
  customersReport: customersReportSchema.optional(),
  sourceBreakdown: z.array(sourceBreakdownSchema).optional(),
  businessInfo: businessInfoSchema.optional(),
  tipReport: tipReportSchema.optional(),
  // Legacy support - will be deprecated
  tipSummary: tipSummarySchema.optional(),
  dailyBreakdown: dailyBreakdownSchema.optional(),
  detailedOrderReport: detailedOrderReportSchema.optional(),

  // New standardized report fields
  customerReport: customerReportSchema.optional(),
  orderBreakdownBySource: orderBreakdownBySourceSchema.optional(),
  orderBreakdownByDate: orderBreakdownByDateSchema.optional(),
  customerBreakdown: customerBreakdownSchema.optional(),
  rewardReport: rewardReportSchema.optional(),
  discountReport: discountReportSchema.optional(),
  couponsReport: couponsReportSchema.optional(),
  orderReport: orderReportSchema.optional(),

  // New POS Performance fields (backward compatible)
  todaysTotalOrders: z.number().optional(),
  subtotal: z.number().optional(),
  tax: z.number().optional(),
  adjustmentsAndFees: z.number().optional(),
  totalDeliveryFeeProcessed: z.number().optional(),
  deliveryFeeBreakdown: deliveryFeeBreakdownSchema.optional(),
  totalTipProcessed: z.number().optional(),
  tipBreakdown: posPerformanceTipBreakdownSchema.optional(),
  totalDiscounts: z.number().optional(),
  totalRefund: z.number().optional(),
  total: z.number().optional(),
  unpaidAmount: z.number().optional(),
  amountCollected: z.number().optional(),
  paymentMethods: paymentMethodsBreakdownSchema.optional(),
  deliveryPaymentMethods: paymentMethodsBreakdownSchema.optional(),
  kitchenTipBreakdown: kitchenTipBreakdownSchema.optional(),
  driverTipBreakdown: driverTipBreakdownSchema.optional(),
  taxBreakdown: taxBreakdownSchema.optional(),
  refundsBreakdown: refundsBreakdownSchema.optional(),
  ordersCoDispatchBreakdown: ordersCoDispatchBreakdownSchema.optional(),
  detailedReport: detailedOrderReportSchema.optional(),
  totalProfitReport: totalProfitReportSchema.optional(),

  // Original request filters for reference
  filters: reportRequestSchema.optional(),
});

// Export the inferred types as regular exports (no TypeScript types)
export const ReportRequest = reportRequestSchema;
export const ReportData = reportDataSchema;
export const PosPerformanceResponse = posPerformanceResponseSchema;
export const BusinessInfo = businessInfoSchema;
export const TenderType = tenderTypeSchema;
export const CardTypeBreakdown = cardTypeBreakdownSchema;
export const SalesBySource = salesBySourceSchema;
export const MoneyReport = moneyReportSchema;
export const DeliveryReport = deliveryReportSchema;
export const CouponsDiscountsRewards = couponsDiscountsRewardsSchema;
export const CustomersReport = customersReportSchema;
export const SourceBreakdown = sourceBreakdownSchema;
export const TipReport = tipReportSchema;
export const TipSummary = tipSummarySchema;
export const TipOverview = tipOverviewSchema;
export const TipByPaymentType = tipByPaymentTypeSchema;
export const TipBySource = tipBySourceSchema;
export const DailyBreakdown = dailyBreakdownSchema;
export const DailyBreakdownItem = dailyBreakdownItemSchema;
export const DailyBreakdownTotals = dailyBreakdownTotalsSchema;
export const DetailedOrderReport = detailedOrderReportSchema;
export const DetailedOrderItem = detailedOrderItemSchema;
export const DetailedOrderSummary = detailedOrderSummarySchema;

// New standardized report types
export const CustomerReport = customerReportSchema;
export const OrderBreakdownBySource = orderBreakdownBySourceSchema;
export const OrderBreakdownByDate = orderBreakdownByDateSchema;
export const CustomerBreakdown = customerBreakdownSchema;
export const RewardReport = rewardReportSchema;
export const DiscountReport = discountReportSchema;
export const CouponsReport = couponsReportSchema;
export const OrderReport = orderReportSchema;
export const TotalProfitReport = totalProfitReportSchema;
export const SalesByCategoryResponse = salesByCategoryResponseSchema;
export const SalesByCategory = salesByCategorySchema;
export const SalesByCategoryItem = salesByCategoryItemSchema;
export const SalesByCategorySummary = salesByCategorySummarySchema;
